import { useEffect, useState } from "react";
import { Clock, MapPin, Play, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PageHero } from "@/components/PageHero";
import { useContent } from "@/lib/content-store";
import { useVisitor } from "@/lib/visitor";
import { IMG } from "@/lib/content";
import { publicApi } from "@/lib/api";
import { describe } from "@/lib/schedule";

const ytId = (url) => url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/|live\/)([\w-]{11})/)?.[1];

const Schedule = ({ c }) => (
  <div className="space-y-3">
    {(c.schedules || []).map((s, i) => (
      <div key={i} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`worship-service-${i}`}>
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#EBF3FA] text-[#0A3A6B]"><Clock className="h-5 w-5" /></span>
        <div><p className="font-display font-semibold text-slate-900">{s.title}</p><p className="text-sm text-slate-500">{describe(s)}{s.note && ` · ${s.note}`}{s.venue && ` · ${s.venue}`}</p></div>
      </div>
    ))}
    <p className="flex items-center gap-2 pt-1 text-sm text-slate-500"><MapPin className="h-4 w-4" /> {c.address}</p>
  </div>
);

const SeriesCard = ({ series, onPlan }) => (
  <div className="grain relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A3A6B] to-[#1D64B4] p-8 text-white sm:p-10" data-testid="current-series">
    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-200"><Sparkles className="h-4 w-4" /> Current Series</p>
    <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl" data-testid="current-series-title">{series.title}</h2>
    <p className="mt-4 max-w-lg whitespace-pre-line leading-relaxed text-sky-50/90" data-testid="current-series-description">{series.description}</p>
    <Button onClick={onPlan} className="mt-8 rounded-full bg-white px-6 text-[#0A3A6B] hover:bg-sky-100" data-testid="worship-plan-visit">Plan your visit <ArrowRight className="ml-1 h-4 w-4" /></Button>
  </div>
);

const PastServices = ({ items, onPlay }) => (
  <section className="mt-16" data-testid="past-services">
    <p className="text-xs font-semibold uppercase tracking-wider text-[#1D64B4]">Watch again</p>
    <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Past services</h2>
    {items.length === 0 ? <p className="mt-6 text-slate-500" data-testid="past-services-empty">Past services will be here for you soon.</p> : (
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((v, i) => (
          <button key={v.id + i} onClick={() => onPlay(v)} className="group text-left" data-testid={`past-service-${i}`}>
            <div className="relative aspect-video overflow-hidden rounded-2xl bg-slate-900">
              <img src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`} alt={v.title} className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-0 grid place-items-center"><span className="grid h-14 w-14 place-items-center rounded-full bg-white/90 text-[#0A3A6B] shadow-lg transition-transform group-hover:scale-110"><Play className="ml-0.5 h-6 w-6 fill-current" /></span></span>
            </div>
            {v.title && <p className="mt-3 font-display font-semibold text-slate-900">{v.title}</p>}
          </button>
        ))}
      </div>
    )}
  </section>
);

export default function Worship() {
  const c = useContent();
  const { openAction } = useVisitor();
  const [playing, setPlaying] = useState(null);
  const [latest, setLatest] = useState([]);
  useEffect(() => { publicApi.get("/public/youtube-latest").then((r) => setLatest(r.data)).catch(() => {}); }, []);
  const pinned = (c.past_services || []).map((v) => ({ ...v, id: ytId(v.url) })).filter((v) => v.id);
  const videos = [...pinned, ...latest.filter((v) => !pinned.some((p) => p.id === v.id))].slice(0, 12);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="worship-page">
      <SiteHeader />
      <PageHero eyebrow="Worship Services" title="Come as you are. There's a seat saved for you." lead="Join us for heartfelt worship, a practical message from God's Word, and a church family glad to welcome you." img={IMG.stage} />
      <main className="mx-auto max-w-7xl px-5 py-14 lg:px-10 lg:py-20">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-start">
          <div><h2 className="mb-6 text-2xl font-bold tracking-tight text-slate-900">Service schedule</h2><Schedule c={c} /></div>
          <SeriesCard series={c.current_series} onPlan={() => openAction("plan_visit")} />
        </div>
        <PastServices items={videos} onPlay={setPlaying} />
      </main>
      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="bg-black p-0 sm:max-w-4xl [&>button]:text-white [&>button]:opacity-90" data-testid="past-service-player">
          <DialogTitle className="sr-only">{playing?.title || "Service video"}</DialogTitle>
          {playing && <iframe title={playing.title || "Service"} className="aspect-video w-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen
            src={`https://www.youtube-nocookie.com/embed/${playing.id}?autoplay=1&rel=0`} />}
        </DialogContent>
      </Dialog>
      <SiteFooter />
    </div>
  );
}
